import {
  Product,
  ProductPost,
  ProductPut,
  ProductTable,
  ProductTablePost,
  ProductTablePut,
} from "callable-data-objects";
import { APIProductTableContext } from "Providers/APIContexts";
import { APIProductTableProps, DefaultProviderProps } from "Providers/types";
import React, { useEffect, useState } from "react";
import { useAPI } from "./APIProvider";

const APIProductTableProvider = ({ children }: DefaultProviderProps) => {
  const api = useAPI();
  const [productTables, setProductTables] = useState<ProductTable[]>([]);
  const [productTable, setProductTable] = useState<ProductTable | undefined>();
  const [product, setProduct] = useState<Product | undefined>();
  const [create, setCreate] = useState<ProductTablePost | undefined>();
  const [createProduct, setCreateProduct] = useState<ProductPost | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  const [search, setSearch] = useState<undefined | string>("");
  const [searchProduct, setSearchProduct] = useState<undefined | string>("");

  async function list() {
    setLoading(true);
    const response = await api.productTableService.list();
    setProductTables(response);
    setLoading(false);
  }

  useEffect(() => {
    list();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  function select(id: string): void {
    setLoading(true);
    api.productTableService.get(id).then((resp: ProductTable) => {
      setProductTable(resp);
      setLoading(false);
    });
  }

  function selectProduct(id: string): void {
    if (productTable) {
      const r = productTable.products.find((r) => r.id === id);
      setProduct(r);
    }
  }

  function unselect() {
    setProductTable(undefined);
    setProduct(undefined);
  }

  function unselectProduct() {
    setProduct(undefined);
  }

  function createProductTable() {
    setCreate({
      name: "",
      description: "",
    });
  }

  function createProductTableProduct() {
    setCreateProduct({
      name: "",
      description: "",
      category: "",
      type: "one-off",
      price: 0.0,
    });
  }

  function uncreate() {
    setCreate(undefined);
    setCreateProduct(undefined);
  }

  function uncreateProduct() {
    setCreateProduct(undefined);
  }

  function validateProductTableName(value: string): string | undefined {
    let error: string | undefined;
    if (value === null || value === "") error = "Name required";
    if (value.length < 3) error = "Name must be 3 or more characters";
    if (
      productTables
        .filter((rt) => productTable && productTable.id !== rt.id)
        .map((rt) => rt.name)
        .indexOf(value) > -1
    ) {
      error = "Name not unique";
    }
    return error;
  }

  async function save(payload: ProductTablePost) {
    if (create) {
      const response = await api.productTableService.post("", payload);
      if (response) {
        list();
        select(response.id);
        uncreate();
      }
    }
  }

  async function saveProduct(payload: ProductPost) {
    if (createProduct && productTable) {
      await api.productTableService.product(productTable.id).post("", payload);
      list();
      select(productTable.id);
      setCreateProduct(undefined);
    }
  }

  async function update(payload: ProductTablePut) {
    if (productTable) {
      await api.productTableService.put(payload.id, payload);
      list();
      select(payload.id);
    }
  }

  async function remove(id: string) {
    await api.productTableService.delete(id);
    list();
    unselect();
  }

  async function removeProduct(id: string) {
    if (productTable) {
      await api.productTableService.product(productTable.id).delete(id);
      list();
      select(productTable.id);
      unselectProduct();
    }
  }

  async function cloneProductTable() {
    if (productTable) {
      const response = await api.productTableService.clone(productTable);
      list();
      if (response instanceof ProductTable) setProductTable(response);
    }
  }

  async function updateProduct(payload: ProductPut) {
    if (productTable && product) {
      await api.productTableService
        .product(productTable.id)
        .put(payload.id, payload)
        .then(() => {
          list();
          select(productTable.id);
          unselectProduct();
        });
    }
  }

  const productTableProps: APIProductTableProps = {
    product,
    productTables,
    productTable,
    create,
    createProduct,
    loading,
    save,
    saveProduct,
    update,
    updateProduct,
    validateProductTableName,
    select,
    selectProduct,
    unselect,
    unselectProduct,
    createProductTable,
    createProductTableProduct,
    uncreate,
    uncreateProduct,
    search,
    setSearch,
    searchProduct,
    setSearchProduct,
    cloneProductTable,
    remove,
    removeProduct,
  };

  return (
    <APIProductTableContext.Provider value={productTableProps}>
      {children}
    </APIProductTableContext.Provider>
  );
};

export default APIProductTableProvider;
